import { EventEmitter } from 'events';
import { useEffect } from 'react';

/**
 * A simple React hook that subscribes to continuous events from an EventEmitter.
 * This is a push-based approach where events are continuously received.
 * If you want to immediately receive the current state, use useEventBusSubscriberMemoized instead.
 *
 * @template EventType - The type of the event payload.
 *
 * @param {EventEmitter} eventBus - The EventEmitter instance used for communication.
 * @param {string} eventName - The event name to listen for incoming events.
 * @param {(event: EventType) => void} handler - A function to handle incoming events.
 *
 * @example
 * useEventBusSubscriberSimple<MyEventType>(
 *   eventBus,
 *   'myEvent',
 *   (event) => {
 *     console.log('Received event:', event);
 *   }
 * );
 */
export function useEventBusSubscriber<EventType>(
  eventBus: EventEmitter,
  eventName: string,
  handler: (event: EventType) => void
) {
  useEffect(() => {
    eventBus.on(eventName, handler);

    return () => {
      eventBus.off(eventName, handler);
    };
  }, [eventBus, eventName, handler]);
}
